import { useMemo } from "react"
import { Link, useParams } from "react-router"
import { useDocumentMeta } from "@client/lib/document-meta"
import { buildEventMeta } from "@shared/pages"
import { usePublicEvent } from "./api"
import { ArrowUpRight } from "./layout"

const EVENT_TYPE_LABELS: Record<string, string> = {
  workshop: "Workshop",
  showcase: "Showcase",
  graduation: "Graduation",
  field_trip: "Field Trip",
  other: "Event",
}

function formatEventDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

function formatTime(time: string): string {
  const [hours, minutes] = time.split(":")
  const h = Number.parseInt(hours || "0", 10)
  const period = h >= 12 ? "PM" : "AM"
  const hour12 = h % 12 || 12
  return `${hour12}:${minutes} ${period}`
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

const FALLBACK_IMAGES = [
  "/images/home/event-bootcamp.png",
  "/images/home/event-summit.png",
  "/images/home/event-arts.png",
]

function EventDetailSkeleton() {
  return (
    <main aria-busy="true" aria-label="Loading event details" className="bg-cream-50">
      <div className="public-container animate-pulse py-8 sm:py-12 lg:py-16">
        <div className="h-4 w-28 rounded-full bg-cream-200" aria-hidden="true" />

        <div className="mt-7 overflow-hidden rounded-[28px] bg-forest-950 sm:mt-9 lg:grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-6 p-7 sm:p-10 lg:p-14" aria-hidden="true">
            <div className="h-7 w-24 rounded-full bg-gold-500/60" />
            <div className="space-y-3">
              <div className="h-10 w-full max-w-md rounded bg-white/20" />
              <div className="h-10 w-4/5 max-w-sm rounded bg-white/20" />
            </div>
            <div className="h-5 w-3/5 max-w-xs rounded bg-white/10" />
          </div>
          <div className="min-h-[260px] bg-forest-800/70 sm:min-h-[360px] lg:min-h-[440px]" aria-hidden="true" />
        </div>

        <div className="mt-5 grid overflow-hidden rounded-2xl border border-ink/10 bg-white sm:grid-cols-3" aria-hidden="true">
          <div className="border-b border-ink/10 p-5 sm:border-b-0 sm:border-r sm:p-6">
            <div className="h-3 w-12 rounded bg-cream-200" />
            <div className="mt-3 h-5 w-4/5 rounded bg-cream-200" />
          </div>
          <div className="border-b border-ink/10 p-5 sm:border-b-0 sm:border-r sm:p-6">
            <div className="h-3 w-12 rounded bg-cream-200" />
            <div className="mt-3 h-5 w-3/5 rounded bg-cream-200" />
          </div>
          <div className="p-5 sm:p-6">
            <div className="h-3 w-14 rounded bg-cream-200" />
            <div className="mt-3 h-5 w-4/5 rounded bg-cream-200" />
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8" aria-hidden="true">
          <div className="rounded-2xl border border-ink/10 bg-white p-6 sm:p-8 lg:p-10">
            <div className="h-3 w-28 rounded bg-cream-200" />
            <div className="mt-4 h-8 w-4/5 max-w-md rounded bg-cream-200" />
            <div className="mt-6 space-y-3">
              <div className="h-4 w-full rounded bg-cream-200" />
              <div className="h-4 w-11/12 rounded bg-cream-200" />
              <div className="h-4 w-3/5 rounded bg-cream-200" />
            </div>
          </div>
          <div className="min-h-[250px] rounded-2xl bg-gold-500/60 p-6 sm:p-8">
            <div className="h-3 w-24 rounded bg-forest-950/15" />
            <div className="mt-4 h-8 w-4/5 rounded bg-forest-950/15" />
            <div className="mt-4 h-4 w-full rounded bg-forest-950/10" />
            <div className="mt-8 h-11 w-32 rounded-full bg-forest-950/20" />
          </div>
        </div>
      </div>
    </main>
  )
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: event, loading, error } = usePublicEvent(id || "")

  const eventMeta = useMemo(
    () =>
      event
        ? buildEventMeta({
            id: event.id,
            title: event.title,
            description: event.description,
            date: new Date(event.date),
            venue: event.venue,
          })
        : null,
    [event],
  )
  useDocumentMeta(eventMeta, { path: id ? `/events/${id}` : "/events" })

  if (loading) {
    return <EventDetailSkeleton />
  }

  if (error || !event) {
    return (
      <div className="bg-cream-50 py-16">
        <div className="public-container">
          <Link to="/events" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-ink/60 hover:text-ink">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Events
          </Link>
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm">
            <p className="text-ink/70">{error || "Event not found"}</p>
          </div>
        </div>
      </div>
    )
  }

  const bannerImage = event.bannerUrl ?? FALLBACK_IMAGES[0]

  return (
    <main className="bg-cream-50">
      <div className="public-container py-8 sm:py-12 lg:py-16">
        <Link to="/events" className="inline-flex items-center gap-2 text-sm font-semibold text-forest-700 transition-colors hover:text-forest-950">
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Events
        </Link>

        <section className="mt-7 overflow-hidden rounded-[28px] bg-forest-950 shadow-[0_20px_60px_rgba(15,58,48,0.14)] sm:mt-9 lg:grid lg:grid-cols-[0.9fr_1.1fr]">
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <span className="w-fit rounded-full bg-gold-500 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-forest-950">
              {EVENT_TYPE_LABELS[event.type] || "Event"}
            </span>
            <h1 className="mt-6 max-w-xl font-display text-3xl font-bold leading-[1.05] text-white sm:text-4xl lg:text-5xl">
              {event.title}
            </h1>
            {event.programName && (
              <p className="mt-5 max-w-md text-base leading-7 text-white/65 sm:text-lg">{event.programName}</p>
            )}
          </div>

          <div className="relative min-h-[260px] overflow-hidden sm:min-h-[360px] lg:min-h-[440px]">
            <img src={bannerImage} alt={event.title} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-forest-950/25 to-transparent lg:from-forest-950/35" />
          </div>
        </section>

        <section aria-label="Event details" className="mt-5 grid overflow-hidden rounded-2xl border border-ink/10 bg-white sm:grid-cols-3">
          <div className="flex items-start gap-3 border-b border-ink/10 p-5 sm:border-b-0 sm:border-r sm:p-6">
            <CalendarIcon className="mt-0.5 h-5 w-5 shrink-0 text-forest-700" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink/45">Date</p>
              <p className="mt-1 text-sm font-semibold leading-6 text-ink">{formatEventDate(event.date)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 border-b border-ink/10 p-5 sm:border-b-0 sm:border-r sm:p-6">
            <ClockIcon className="mt-0.5 h-5 w-5 shrink-0 text-forest-700" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink/45">Time</p>
              <p className="mt-1 text-sm font-semibold leading-6 text-ink">
                {formatTime(event.startTime)}
                {event.endTime && ` - ${formatTime(event.endTime)}`}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-5 sm:p-6">
            <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-forest-700" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-ink/45">Venue</p>
              <p className="mt-1 text-sm font-semibold leading-6 text-ink">{event.venue || "Details to be announced"}</p>
            </div>
          </div>
        </section>

        <div className="mt-10 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
          <article className="rounded-2xl border border-ink/10 bg-white p-6 sm:p-8 lg:p-10">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-forest-700">About the event</p>
            <h2 className="mt-3 font-display text-2xl font-bold text-forest-950 sm:text-3xl">Make room for what&apos;s next.</h2>
            {event.description ? (
              <p className="mt-5 max-w-2xl whitespace-pre-wrap text-base leading-8 text-ink/75">{event.description}</p>
            ) : (
              <p className="mt-5 max-w-2xl text-base leading-8 text-ink/65">Join the Redempta community for a meaningful event built around learning, connection, and shared possibility.</p>
            )}
          </article>

          <aside className="flex flex-col justify-between rounded-2xl bg-gold-500 p-6 sm:p-8">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-forest-800">Stay connected</p>
              <h2 className="mt-3 font-display text-2xl font-bold leading-tight text-forest-950">Interested in attending?</h2>
              <p className="mt-3 text-sm leading-6 text-ink/75">Get in touch with us for more information about this event.</p>
            </div>
            <Link
              to="/contact"
              className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-forest-950 py-2 pl-5 pr-2 text-base font-semibold text-white transition-colors hover:bg-forest-800"
            >
              Contact Us
              <span className="grid h-8 w-8 place-items-center rounded-full bg-white text-forest-950">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          </aside>
        </div>
      </div>
    </main>
  )
}
